/* ---------------------------------------------------------------------
   CUSTOM CONNECTOR LINE — "Shop By Category" tiles
   Draws one glowing animated line connecting the category tiles inside
   .tt-categories-listing, in reading order (left-to-right per row, then
   down to the next row), snaking through each tile's centre point.
   Confined to this one section only — does not extend to any other
   part of the page. Recalculates on load, on window resize, and after
   fonts/images finish loading (tile positions can shift once images
   load in), so the line stays aligned with the tiles at every screen
   size.
   --------------------------------------------------------------------- */
(function () {
  "use strict";

  function makeGradientId() {
    return "js-connector-line-gradient-" + Math.random().toString(36).slice(2, 9);
  }

  function ensureSvg(container, svgClass, pathExtraClass) {
    var svg = container.querySelector(":scope > svg." + svgClass);
    if (!svg) {
      svg = document.createElementNS("http://www.w3.org/2000/svg", "svg");
      svg.setAttribute("class", "js-connector-line-svg " + svgClass);
      svg.setAttribute("preserveAspectRatio", "none");
      container.insertBefore(svg, container.firstChild);

      var gradId = makeGradientId();

      var defs = document.createElementNS("http://www.w3.org/2000/svg", "defs");
      var gradient = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "linearGradient"
      );
      gradient.setAttribute("id", gradId);
      gradient.setAttribute("gradientUnits", "userSpaceOnUse");
      gradient.setAttribute("class", "js-connector-line-gradient-anim");

      var stops = [
        ["0%", "#ff5a3c"],
        ["17%", "#edff1a"],
        ["34%", "#08f408"],
        ["50%", "#2879fe"],
        ["67%", "#a855f7"],
        ["84%", "#ff2d78"],
        ["100%", "#ff5a3c"]
      ];
      stops.forEach(function (s) {
        var stop = document.createElementNS(
          "http://www.w3.org/2000/svg",
          "stop"
        );
        stop.setAttribute("offset", s[0]);
        stop.setAttribute("stop-color", s[1]);
        gradient.appendChild(stop);
      });

      defs.appendChild(gradient);
      svg.appendChild(defs);

      var path0 = document.createElementNS(
        "http://www.w3.org/2000/svg",
        "path"
      );
      path0.setAttribute("class", "js-connector-line-path" + (pathExtraClass ? " " + pathExtraClass : ""));
      path0.setAttribute("stroke", "url(#" + gradId + ")");
      svg.appendChild(path0);
    }
    return svg;
  }

  function buildConnectorFor(container) {
    var tiles = Array.prototype.slice.call(
      container.querySelectorAll(".tt-items-categories")
    );
    if (tiles.length < 2) return;

    if (getComputedStyle(container).position === "static") {
      container.style.position = "relative";
    }

    var svg = ensureSvg(container, "js-category-connector-svg");
    var path = svg.querySelector("path.js-connector-line-path");
    if (!path) {
      path = document.createElementNS("http://www.w3.org/2000/svg", "path");
      path.setAttribute("class", "js-connector-line-path");
      svg.appendChild(path);
    }

    var rect = container.getBoundingClientRect();
    var width = container.clientWidth;
    var height = container.clientHeight;
    if (!width || !height) return;

    svg.setAttribute("width", width);
    svg.setAttribute("height", height);
    svg.setAttribute("viewBox", "0 0 " + width + " " + height);

    var gradient = svg.querySelector("linearGradient");
    if (gradient) {
      gradient.setAttribute("x1", -width);
      gradient.setAttribute("x2", width * 2);
      gradient.setAttribute("y1", 0);
      gradient.setAttribute("y2", 0);
    }

    // Group tiles into visual rows by their vertical position (they wrap
    // responsively, so row membership isn't fixed).
    var points = tiles.map(function (tile) {
      var r = tile.getBoundingClientRect();
      return {
        x: r.left - rect.left + r.width / 2,
        y: r.top - rect.top + r.height / 2,
        top: r.top - rect.top
      };
    });

    var rows = [];
    points.forEach(function (p) {
      var row = rows.find(function (rw) {
        return Math.abs(rw.top - p.top) < 12;
      });
      if (row) {
        row.items.push(p);
      } else {
        rows.push({ top: p.top, items: [p] });
      }
    });
    rows.sort(function (a, b) { return a.top - b.top; });

    // Snake left-to-right, then right-to-left on alternating rows, so the
    // line travels a sensible path through every tile without crossing
    // back over itself.
    var ordered = [];
    rows.forEach(function (row, i) {
      row.items.sort(function (a, b) { return a.x - b.x; });
      if (i % 2 === 1) row.items.reverse();
      ordered = ordered.concat(row.items);
    });

    drawPath(path, ordered);
  }

  // Used only by the category-tile snake above: overshoots the control
  // points on purpose so a line snaking between *rows* (large vertical
  // jumps) still reads as one smooth curve rather than sharp corners.
  // Do not reuse this for a single roughly-horizontal row of points —
  // see drawSmoothPath below, built for that case instead.
  function drawPath(path, ordered) {
    if (ordered.length < 2) {
      path.removeAttribute("d");
      return;
    }
    var d = "M " + ordered[0].x + " " + ordered[0].y;
    for (var i = 1; i < ordered.length; i++) {
      var prev = ordered[i - 1];
      var curr = ordered[i];
      // Smooth curve between points rather than sharp right angles.
      var midX = (prev.x + curr.x) / 2;
      var midY = (prev.y + curr.y) / 2;
      d += " Q " + prev.x + " " + curr.y + " " + midX + " " + midY;
      d += " Q " + curr.x + " " + prev.y + " " + curr.x + " " + curr.y;
    }
    path.setAttribute("d", d);
  }

  // Gentle single-row curve: control points are derived from the points'
  // own neighbours (Catmull-Rom-ish) rather than swapping in the *other*
  // point's coordinate, so it never overshoots into a big vertical bulge
  // when y barely changes between points (e.g. a horizontal row of
  // product-card centres). Falls back to a straight line between two
  // points.
  function drawSmoothPath(path, ordered) {
    if (ordered.length < 2) {
      path.removeAttribute("d");
      return;
    }
    if (ordered.length === 2) {
      path.setAttribute(
        "d",
        "M " + ordered[0].x + " " + ordered[0].y +
        " L " + ordered[1].x + " " + ordered[1].y
      );
      return;
    }
    var d = "M " + ordered[0].x + " " + ordered[0].y;
    for (var i = 0; i < ordered.length - 1; i++) {
      var p0 = ordered[i - 1] || ordered[i];
      var p1 = ordered[i];
      var p2 = ordered[i + 1];
      var p3 = ordered[i + 2] || p2;

      // Catmull-Rom to cubic Bezier conversion (standard 1/6 tangent).
      var c1x = p1.x + (p2.x - p0.x) / 6;
      var c1y = p1.y + (p2.y - p0.y) / 6;
      var c2x = p2.x - (p3.x - p1.x) / 6;
      var c2y = p2.y - (p3.y - p1.y) / 6;

      d += " C " + c1x + " " + c1y + " " + c2x + " " + c2y + " " + p2.x + " " + p2.y;
    }
    path.setAttribute("d", d);
  }

  function buildAllCategoryConnectors() {
    var containers = document.querySelectorAll(".tt-categories-listing .row");
    containers.forEach ? containers.forEach(buildConnectorFor) : Array.prototype.forEach.call(containers, buildConnectorFor);
  }

  /* ---------------------------------------------------------------------
     V33 — PRODUCT CONNECTOR REMOVED
     The animated rainbow line that used to run behind homepage product
     cards has been retired completely. Category connector behaviour is
     intentionally untouched. Clean up any SVG/host class left by an
     older cached render so no faint row-wide box/line can remain.
     --------------------------------------------------------------------- */
  function removeProductConnectors() {
    var carousels = document.querySelectorAll(".tt-carousel-products-js");
    Array.prototype.forEach.call(carousels, function (container) {
      container.classList.remove("js-product-connector-host");
      var svgs = container.querySelectorAll("svg.js-product-connector-svg");
      Array.prototype.forEach.call(svgs, function (svg) {
        if (svg.parentNode) svg.parentNode.removeChild(svg);
      });
    });
  }

  /* ---------------------------------------------------------------------
     ROW EQUALISERS — live DOM measurement is more reliable than CSS-only
     stretching for these specific Shopify blocks because carousel clones,
     lazy-loaded imagery, and mixed title lengths can all shift the final
     box height after first paint.

     IMPORTANT (Round 13 fix): equalizeProductRows() used to also set an
     inline min-height on the outer `.tt-product.product-parent` card —
     the *whole* card, including the carousel's own clipping wrapper.
     That duplicated what CSS already does via `.slick-track{display:flex}`
     (custom-redesign.css, section 31/47), and because this JS runs on
     "load", on every window resize, AND on every MutationObserver
     mutation (including the style mutations this very function makes),
     a tall measurement taken before a lazy image finished loading could
     get "locked in" as an inline min-height that CSS `!important` rules
     could not reliably out-prioritise afterwards (inline styles beat
     stylesheet rules unless the stylesheet also uses `!important`, and
     several later cleanup passes were fighting this same inline value).
     That is the root cause of the large empty space beneath the homepage
     product rows. The card's own height is left to flex/CSS now; only
     the small yellow info panel (title/price plate) is still equalised
     here, since evening out just that internal plate is purely
     cosmetic and can never expand the outer, clipped carousel area.
     --------------------------------------------------------------------- */
  function equalizeCategoryRows() {
    var rows = document.querySelectorAll('.tt-categories-listing .row');
    Array.prototype.forEach.call(rows, function (row) {
      var cols = Array.prototype.slice.call(row.children).filter(function (col) {
        return col.querySelector('.tt-items-categories');
      });
      if (!cols.length) return;

      cols.forEach(function (col) {
        var card = col.querySelector('.tt-items-categories');
        if (card) card.style.minHeight = '';
      });

      var grouped = [];
      cols.forEach(function (col) {
        var top = col.getBoundingClientRect().top;
        var bucket = grouped.find(function (g) { return Math.abs(g.top - top) < 12; });
        if (!bucket) {
          bucket = { top: top, items: [] };
          grouped.push(bucket);
        }
        bucket.items.push(col);
      });

      grouped.forEach(function (group) {
        var max = 0;
        group.items.forEach(function (col) {
          var card = col.querySelector('.tt-items-categories');
          if (card) max = Math.max(max, card.offsetHeight);
        });
        if (!max) return;
        group.items.forEach(function (col) {
          var card = col.querySelector('.tt-items-categories');
          if (card) card.style.minHeight = max + 'px';
        });
      });
    });
  }

  function equalizeProductRows() {
    var carousels = document.querySelectorAll('.tt-carousel-products-js');
    Array.prototype.forEach.call(carousels, function (container) {
      var rect = container.getBoundingClientRect();
      var cards = Array.prototype.slice.call(container.querySelectorAll('.forsliders-margins')).filter(function (el) {
        var slide = el.closest('.slick-slide');
        if (slide && slide.classList.contains('slick-cloned')) return false;
        var r = el.getBoundingClientRect();
        if (!r.width || !r.height) return false;
        var visibleLeft = Math.max(r.left, rect.left);
        var visibleRight = Math.min(r.right, rect.right);
        var visibleWidth = visibleRight - visibleLeft;
        return visibleWidth >= r.width * 0.5;
      });
      if (!cards.length) return;

      // Only the inner info panel (title/price plate) is equalised here.
      // The outer card's height is intentionally left alone — CSS flex
      // (`.slick-track{display:flex;align-items:stretch}`) already keeps
      // every card in a row the same height without an inline min-height
      // that could get stuck too tall (see comment above).
      cards.forEach(function (cardWrap) {
        var panel = cardWrap.querySelector('.tt-card-info-panel');
        if (panel) panel.style.minHeight = '';
      });

      var maxPanel = 0;
      cards.forEach(function (cardWrap) {
        var panel = cardWrap.querySelector('.tt-card-info-panel');
        if (panel) maxPanel = Math.max(maxPanel, panel.offsetHeight);
      });

      cards.forEach(function (cardWrap) {
        var panel = cardWrap.querySelector('.tt-card-info-panel');
        if (panel && maxPanel) panel.style.minHeight = maxPanel + 'px';
      });
    });
  }



  function buildAll() {
    buildAllCategoryConnectors();
    removeProductConnectors();
    equalizeCategoryRows();
    equalizeProductRows();
  }

  function debounce(fn, wait) {
    var t;
    return function () {
      clearTimeout(t);
      t = setTimeout(fn, wait);
    };
  }

  var debouncedBuild = debounce(buildAll, 150);

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", buildAll);
  } else {
    buildAll();
  }
  window.addEventListener("load", buildAll);
  window.addEventListener("resize", debouncedBuild);

  // Tile/card images load lazily and can change row heights/positions
  // after the initial layout pass — rebuild once each image settles.
  document.addEventListener(
    "load",
    function (e) {
      if (
        e.target &&
        e.target.closest &&
        (e.target.closest(".tt-categories-listing") || e.target.closest(".tt-carousel-products-js"))
      ) {
        debouncedBuild();
      }
    },
    true
  );

  // Slick (jQuery) fires these on every slide/scroll/breakpoint change,
  // and rebuilds/re-clones its DOM — listen via jQuery when present so
  // the product connector line stays correctly positioned as the
  // carousel is dragged or paged through.
  function bindSlickEvents() {
    if (typeof window.jQuery === "undefined") return false;
    var $ = window.jQuery;
    $(document).on(
      "afterChange setPosition breakpoint reInit",
      ".tt-carousel-products-js",
      debouncedBuild
    );
    // Slick emits these on the slider element itself, so also bind
    // directly to any carousel already on the page (covers sliders
    // initialised before this script ran).
    $(".tt-carousel-products-js").each(function () {
      $(this).on(
        "afterChange.jsConnector setPosition.jsConnector breakpoint.jsConnector reInit.jsConnector",
        debouncedBuild
      );
    });
    return true;
  }

  if (!bindSlickEvents()) {
    // jQuery/slick may not be ready yet — retry briefly after load.
    window.addEventListener("load", function () {
      setTimeout(bindSlickEvents, 300);
    });
  }

  // Fallback for browsers/edge-cases where the slick events above don't
  // fire as expected: watch the carousel track for DOM mutations (slick
  // rewrites transform/classes on drag and on slide change) and rebuild,
  // debounced, whenever that happens.

  /* ---------------------------------------------------------------------
     FOOTER SOCIAL RELOCATION — move the existing newsletter-row social
     links into the green Contact Us column, directly after its contact
     details. This moves (not clones) the original list, preserving every
     existing href, target and icon class from Shopify settings.
     --------------------------------------------------------------------- */
  function moveFooterSocialsToContact() {
    var socials = document.querySelector(".tt-footer-custom .tt-social-icon");
    if (!socials || socials.classList.contains("tt-footer-contact-socials")) return;

    var mailLink = document.querySelector('.tt-footer-col .tt-collapse-content a[href^="mailto:"]');
    if (!mailLink) return;

    var contactContent = mailLink.closest(".tt-collapse-content");
    if (!contactContent) return;

    socials.classList.add("tt-footer-contact-socials");
    contactContent.appendChild(socials);

    var oldRight = document.querySelector(".tt-footer-custom .tt-col-right");
    if (oldRight && !oldRight.querySelector(".tt-social-icon")) {
      oldRight.style.display = "none";
    }
  }


  function decorateFooterPanels() {
    var footerCol = document.querySelector('.tt-footer-col, .tt-footer-center');
    if (!footerCol) return;

    Array.prototype.forEach.call(footerCol.querySelectorAll('.tt-mobile-collapse'), function (collapse) {
      var titleEl = collapse.querySelector('.tt-collapse-title');
      var title = titleEl ? titleEl.textContent.trim().toLowerCase() : '';
      var column = collapse.closest('[class*="col-"]') || collapse.parentElement;
      var content = collapse.querySelector('.tt-collapse-content');

      if ((content && content.querySelector('a[href^="mailto:"]')) || /company house/i.test(collapse.textContent || '')) {
        collapse.classList.add('tt-footer-contact-panel');
        if (column) column.classList.add('tt-footer-contact-column');
      }
    });
  }


  function decorateProductBundleWidgets() {
    var productInfo = document.querySelector('.tt-product-single-info');
    if (!productInfo) return;

    var probes = Array.prototype.slice.call(productInfo.querySelectorAll('h1,h2,h3,h4,h5,h6,strong,span,div,p,label'));
    var marked = [];

    function normalise(str){
      return String(str || '').replace(/\s+/g, ' ').trim().toUpperCase();
    }

    function markWidget(node){
      if (!node || node.nodeType !== 1) return;
      if (marked.indexOf(node) !== -1) return;
      if (!node.querySelector('input, select, button, [role="button"]')) return;
      node.classList.add('js-product-bundle-widget');
      marked.push(node);
    }

    probes.forEach(function (probe) {
      var text = normalise(probe.textContent);
      if (!text) return;
      if (text.indexOf('BUNDLE & SAVE') === -1 && text.indexOf('BUNDLE AND SAVE') === -1 && text.indexOf('MOST POPULAR') === -1) return;

      var parent = probe.closest('div');
      var candidates = [
        probe.nextElementSibling,
        parent,
        parent && parent.nextElementSibling,
        parent && parent.parentElement,
        probe.parentElement,
        probe.parentElement && probe.parentElement.parentElement
      ].filter(Boolean);

      candidates.forEach(function (candidate) {
        if (!candidate) return;
        if (candidate.querySelector('input, select, button, [role="button"]')) {
          markWidget(candidate);
        }
      });
    });
  }

  function moveFooterSharkToContact() {
    var footerCol = document.querySelector('.tt-footer-col, .tt-footer-center');
    var copyrightBar = document.querySelector('.tt-footer-custom.tt-color-scheme-04');
    if (!footerCol || !copyrightBar) return;

    // Prefer the real fourth/footer contact column. If the older detection
    // has not tagged it yet, use the last visible footer column as fallback.
    var contactColumn = footerCol.querySelector('.tt-footer-contact-column');
    if (!contactColumn) {
      var footerRow = footerCol.querySelector('.row');
      var cols = footerRow ? Array.prototype.filter.call(footerRow.children, function(el){ return el.nodeType === 1; }) : [];
      contactColumn = cols.length ? cols[cols.length - 1] : null;
      if (contactColumn) contactColumn.classList.add('tt-footer-contact-column');
    }
    if (!contactColumn || contactColumn.querySelector('.tt-footer-shark-holder')) return;

    // The browsing shark is the footer logo itself in this theme. Earlier
    // code explicitly excluded .tt-logo, which is why it stayed in its own
    // giant black row. Move that logo image first; only fall back to another
    // image if the logo is unavailable.
    var sharkImg = copyrightBar.querySelector('.tt-logo img');
    if (!sharkImg) {
      var candidates = Array.prototype.slice.call(copyrightBar.querySelectorAll('img'));
      if (!candidates.length) return;
      candidates.sort(function (a, b) {
        var areaA = (a.naturalWidth || a.width || a.clientWidth || 0) * (a.naturalHeight || a.height || a.clientHeight || 0);
        var areaB = (b.naturalWidth || b.width || b.clientWidth || 0) * (b.naturalHeight || b.height || b.clientHeight || 0);
        return areaB - areaA;
      });
      sharkImg = candidates[0];
    }
    if (!sharkImg) return;

    var originalLogoCol = sharkImg.closest('.tt-logo-col');
    var holder = document.createElement('div');
    holder.className = 'tt-footer-shark-holder';
    sharkImg.classList.add('tt-footer-shark-art');
    holder.appendChild(sharkImg);
    contactColumn.appendChild(holder);
    if (originalLogoCol) originalLogoCol.classList.add('js-footer-logo-moved');
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", function () {
      decorateFooterPanels();
      moveFooterSocialsToContact();
      moveFooterSharkToContact();
      decorateProductBundleWidgets();
      equalizeCategoryRows();
      equalizeProductRows();
    });
  } else {
    decorateFooterPanels();
    moveFooterSocialsToContact();
    moveFooterSharkToContact();
    decorateProductBundleWidgets();
    equalizeCategoryRows();
    equalizeProductRows();
  }
  window.addEventListener("load", function () {
    decorateFooterPanels();
    moveFooterSocialsToContact();
    moveFooterSharkToContact();
    decorateProductBundleWidgets();
    equalizeCategoryRows();
    equalizeProductRows();
  });

  window.setTimeout(decorateProductBundleWidgets, 1200);
  window.setTimeout(decorateProductBundleWidgets, 2600);

  if (window.MutationObserver) {
    var observeWhenReady = function () {
      document.querySelectorAll(".tt-carousel-products-js").forEach(function (el) {
        var mo = new MutationObserver(debouncedBuild);
        mo.observe(el, { attributes: true, attributeFilter: ["style", "class"], subtree: true, childList: true });
      });
      var productInfo = document.querySelector('.tt-product-single-info');
      if (productInfo && !productInfo.__jsBundleObserverBound) {
        var bundleObserver = new MutationObserver(function(){
          decorateProductBundleWidgets();
        });
        bundleObserver.observe(productInfo, { childList: true, subtree: true });
        productInfo.__jsBundleObserverBound = true;
      }
    };
    if (document.readyState === "loading") {
      document.addEventListener("DOMContentLoaded", observeWhenReady);
    } else {
      observeWhenReady();
    }
  }
})();
